import { assign, createMachine, DoneInvokeEvent, MachineConfig } from 'xstate'
import { partition } from 'lodash/fp'
import { ExternalLinkReviewAsset, ReviewAsset, ReviewAssetType, ReviewerRole, ReviewPrivacy, ReviewType } from './types'
import {
  accessGroupsToUserAndChannelMembers,
  isAccessMemberGroupValue,
  uniqueRecipients,
  Value,
} from 'components/ShareSheet/RecipientSelector/Types'
import { ArticleFlat, ReviewMembershipType, RecipientsSelectorValueType } from 'types'
import { SubmitHandler } from './useSubmit'
import dayjs from 'dayjs'
import { Context, EventId, ReviewWizardEvent, State, StateId } from './machineTypes'
import { DEFAULT_DOC_TITLE } from 'utils/constants'
import { fetchReviewTypeConfig } from './fetchReviewTypeConfig'
import { assignTemplate, clearReviewTemplateAssigns } from './reviewTemplates'
import { reviewRolesFromReviewTypeAndConfig, ReviewTypeConfig } from './reviewConfig'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useIsNotSelf } from 'hooks/useIsNotSelf'
import { TemplateFormData } from 'components/ReviewTemplateFormModal/Types'
import { reviewTitle } from './reviewTitle'

export type MemberFilter = ReturnType<typeof useIsNotSelf>

export interface Props {
  onSubmit: SubmitHandler
  canChangePrivacy: boolean
  onRequestSuggestion?: (suggester: Value, addMember: boolean, title?: string, message?: string) => void
  isNotSelf: MemberFilter
  disableTypeSelection?: boolean
}

const templateFormMembersFromContext = (type: ReviewMembershipType, context: Context) => {
  const [accessGroupMembers, otherMembers] = partition(isAccessMemberGroupValue, context[`${type}s`])
  return uniqueRecipients([...otherMembers, ...accessGroupsToUserAndChannelMembers(accessGroupMembers)])
}

export const templateFormDataFromContext = (context: Context): TemplateFormData => ({
  name: '',
  private: context.reviewPrivacy === ReviewPrivacy.PRIVATE,
  description: context.message,
  html_description: context.htmlMessage || null,
  days_ahead: dayjs(context.endDate).diff(dayjs().format('YYYY-MM-DD'), 'days'),
  viewers: templateFormMembersFromContext('viewer', context),
  reviewers: templateFormMembersFromContext('reviewer', context),
  approvers: templateFormMembersFromContext('approver', context),
})

export const machine = ({
  onSubmit,
  canChangePrivacy,
  onRequestSuggestion = () => {},
  isNotSelf,
  disableTypeSelection,
}: Props) => {
  const submitAction = (context: Context, addMembers = false) => {
    onSubmit({
      asset: context.asset as ReviewAsset,
      assetType: context.assetType as ReviewAssetType,
      addMembers: addMembers,
      recipients: [...context.reviewers, ...context.approvers],
      approvers: context.approvers,
      viewers: context.viewers,
      title: context.title,
      message: context.message,
      htmlMessage: context.htmlMessage,
      endDate: context.endDate,
      useReviewLayer: false,
      reviewTemplateId: context.reviewTemplate?.id || undefined,
      interval: context.interval,
      layerId: context.layerId,
      frequency: context.frequency,
      weekdays: context.weekdays,
    })
  }

  const fetchAndAssignConfigAction = (target: StateId, id: string) => ({
    id,
    src: (context: Context, _: ReviewWizardEvent) =>
      fetchReviewTypeConfig({
        articleId: context.assetType === ReviewAssetType.Article ? (context.asset as ArticleFlat).id : null,
        reviewType: context.reviewType || ReviewType.FEEDBACK,
        isNotSelf,
      }),
    onDone: {
      target,
      actions: [
        assign<Context, DoneInvokeEvent<ReviewTypeConfig>>({
          reviewers: (_, event) => event.data.reviewers,
          approvers: (_, event) => event.data.approvers,
          viewers: (_, event) => event.data.viewers ?? [],
          reviewRoles: (context, event) =>
            reviewRolesFromReviewTypeAndConfig(context.reviewType || ReviewType.FEEDBACK, event.data),
        }),
      ],
    },
    onError: {
      target,
      actions: () => showErrorMessageToast('There was a problem loading the review config.'),
    },
  })

  const aWeekFromNow = dayjs().add(7, 'day').format('YYYY-MM-DD')
  const defaultTitle = reviewTitle(DEFAULT_DOC_TITLE, ReviewType.FEEDBACK)

  const clearReviewTemplate = clearReviewTemplateAssigns(defaultTitle, aWeekFromNow)

  return createMachine<Context, ReviewWizardEvent, State>({
    schema: {
      context: {} as Context,
      events: {} as ReviewWizardEvent,
    },
    id: 'reviewsWizard',
    initial: StateId.FILE_SELECTION,
    context: {
      disableTypeSelection,
      title: defaultTitle,
      reviewType: null,
      reviewPrivacy: ReviewPrivacy.PUBLIC,
      reviewers: [],
      approvers: [],
      viewers: [],
      suggesters: [],
      message: '',
      interval: 'P0D',
      weekdays: [],
      endDate: aWeekFromNow,
      asset: null,
      reviewTemplate: null,
      reviewRoles: [ReviewerRole.REVIEWER],
      assetType: null,
    },
    states: {
      [StateId.FILE_SELECTION]: {
        on: {
          [EventId.CONTINUE]: StateId.TYPE_SELECTION,
          [EventId.SET_ASSET]: {
            actions: [
              assign({ asset: (context, event) => event.asset }),
              assign({ assetType: (context, event) => event.assetType }),
              assign({
                title: (context, event) =>
                  event.asset?.title ? reviewTitle(event.asset.title, ReviewType.FEEDBACK) : '',
              }),
            ],
          },
          [EventId.SET_ASSET_TITLE]: {
            actions: [
              assign({
                asset: (context, event) => {
                  const asset =
                    context.assetType === ReviewAssetType.ExternalLink
                      ? (context.asset as ExternalLinkReviewAsset)
                      : { link: '', icon: '' }
                  const { icon, link } = asset
                  return {
                    title: event.assetTitle,
                    link,
                    icon,
                  }
                },
              }),
            ],
          },
          [EventId.SET_EXTERNAL_FILE_ICON]: {
            actions: [
              assign({
                asset: (context: Context, event) => {
                  const asset =
                    context.assetType === ReviewAssetType.ExternalLink
                      ? (context.asset as ExternalLinkReviewAsset)
                      : { link: '', title: '' }
                  const { title, link } = asset
                  return {
                    title,
                    link,
                    icon: event.externalFileIcon,
                  }
                },
                assetType: ReviewAssetType.ExternalLink,
              }),
            ],
          },
          [EventId.SET_EXTERNAL_LINK]: {
            actions: [
              assign({
                asset: (context, event) => {
                  const asset =
                    context.assetType === ReviewAssetType.ExternalLink
                      ? (context.asset as ExternalLinkReviewAsset)
                      : { icon: '', title: '' }
                  const { title, icon } = asset
                  return {
                    title,
                    icon,
                    link: event.externalLink,
                  }
                },
                assetType: ReviewAssetType.ExternalLink,
              }),
            ],
          },
          [EventId.SELECT_REVIEWERS]: {
            actions: [assign({ reviewers: (context, event) => event.reviewers })],
          },
          [EventId.SELECT_APPROVERS]: {
            actions: [assign({ approvers: (context, event) => event.approvers })],
          },
          [EventId.CLEAR]: {
            actions: [
              assign({ asset: (_) => null }),
              assign({ reviewers: (_) => [] }),
              assign({ approvers: (_) => [] }),
              assign({ viewers: (_) => [] }),
            ],
          },
        },
      },
      [StateId.TYPE_SELECTION]: {
        always: [
          {
            target: StateId.TYPE_SELECTION_FETCHING_REVIEW_TYPE_CONFIG,
            cond: (context) => {
              return !context.reviewType
            },
          },
        ],
        on: {
          [EventId.BACK]: {
            target: StateId.FILE_SELECTION,
            actions: [assign({ asset: (_) => null }), assign({ assetType: (_) => null })],
          },
          [EventId.CONTINUE]: [
            {
              target: StateId.REVIEWERS_SELECTION,
              cond: (context, event) => context.reviewTemplate !== null && !event.fetchConfig,
            },
            {
              target: StateId.REVIEWERS_SELECTION,
              cond: (context, event) => context.reviewType !== ReviewType.SUGGESTIONS && !event.fetchConfig,
            },
            {
              target: StateId.SUGGESTERS_SELECTION,
              cond: (context, event) => context.reviewType === ReviewType.SUGGESTIONS && !event.fetchConfig,
            },
            {
              target: StateId.REVIEWERS_SELECTION_FETCHING_REVIEW_TYPE_CONFIG,
              cond: (context, event) => event.fetchConfig,
            },
          ],
          [EventId.SELECT_TYPE]: {
            target: StateId.TYPE_SELECTION_FETCHING_REVIEW_TYPE_CONFIG,
            actions: [
              assign({ reviewType: (context, event) => event.reviewType }),
              assign({
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                title: (context, event) => reviewTitle(context.asset!.title, event.reviewType),
              }),
            ],
          },
          [EventId.SELECT_REVIEWERS]: {
            actions: [assign({ reviewers: (context, event) => event.reviewers })],
          },
          [EventId.SELECT_APPROVERS]: {
            actions: [assign({ approvers: (context, event) => event.approvers })],
          },
          [EventId.SELECT_REVIEW_TEMPLATE]: {
            actions: [assignTemplate(canChangePrivacy)],
          },
          [EventId.VIEW_ALL_TEMPLATES]: StateId.ALL_TEMPLATES,
        },
      },
      [StateId.ALL_TEMPLATES]: {
        on: {
          [EventId.SELECT_REVIEW_TEMPLATE]: {
            actions: [assignTemplate(canChangePrivacy)],
            target: StateId.REVIEWERS_SELECTION,
          },
          [EventId.BACK]: StateId.TYPE_SELECTION,
        },
      },
      [StateId.TYPE_SELECTION_FETCHING_REVIEW_TYPE_CONFIG]: {
        entry: [
          assign({
            reviewType: (context) => {
              return context.reviewType || ReviewType.FEEDBACK
            },
          }),
        ],
        invoke: [fetchAndAssignConfigAction(StateId.TYPE_SELECTION, 'fetch-review-type-config')],
      },
      [StateId.REVIEWERS_SELECTION_FETCHING_REVIEW_TYPE_CONFIG]: {
        entry: [
          assign({
            reviewType: (context) => {
              return context.reviewType || ReviewType.FEEDBACK
            },
          }),
        ],
        invoke: [
          fetchAndAssignConfigAction(StateId.REVIEWERS_SELECTION, 'reviewers-selection-fetch-review-type-config'),
        ],
      },
      [StateId.REVIEWERS_SELECTION]: {
        on: {
          [EventId.BACK]: [
            {
              target: StateId.TYPE_SELECTION,
              cond: (context) => context.reviewTemplate !== null,
              actions: [clearReviewTemplate, assign({ interval: 'P0D', weekdays: [], frequency: undefined })],
            },
            {
              target: StateId.TYPE_SELECTION_FETCHING_REVIEW_TYPE_CONFIG,
              actions: [clearReviewTemplate, assign({ interval: 'P0D', weekdays: [], frequency: undefined })],
            },
          ],
          [EventId.CONTINUE]: [
            {
              target: StateId.INVITE_EMAILS,
              cond: (_, event) => !!event.newEmails && event.newEmails.length > 0,
            },
            {
              actions: [(context) => submitAction(context, false)],
              cond: (_, event) => !event.newEmails || event.newEmails.length < 1,
            },
          ],
          [EventId.SELECT_REVIEWERS]: {
            actions: [assign({ reviewers: (context, event) => event.reviewers })],
          },
          [EventId.SELECT_APPROVERS]: {
            actions: [
              assign({ approvers: (context, event) => event.approvers }),
              assign({
                reviewRoles: (context, event) =>
                  event.approvers.length > 0 && !context.reviewRoles.includes(ReviewerRole.APPROVER)
                    ? [...context.reviewRoles, ReviewerRole.APPROVER]
                    : [...context.reviewRoles],
              }),
            ],
          },
          [EventId.SELECT_VIEWERS]: {
            actions: [
              assign({ viewers: (context, event) => event.viewers }),
              assign({
                reviewRoles: (context, event) =>
                  event.viewers.length > 0 && !context.reviewRoles.includes(ReviewerRole.VIEWER)
                    ? [...context.reviewRoles, ReviewerRole.VIEWER]
                    : [...context.reviewRoles],
              }),
            ],
          },
          [EventId.SET_MESSAGE]: {
            actions: [
              assign({ message: (context, event) => event.message }),
              assign({ htmlMessage: (context, event) => event.htmlMessage }),
            ],
          },
          [EventId.SET_TITLE]: {
            actions: [assign({ title: (context, event) => event.title })],
          },
          [EventId.SET_END_DATE]: {
            actions: [assign({ endDate: (context, event) => event.endDate })],
          },
          [EventId.SELECT_PRIVACY]: {
            actions: [
              assign({
                reviewPrivacy: (context, event) => (canChangePrivacy ? event.reviewPrivacy : ReviewPrivacy.PUBLIC),
              }),
            ],
          },
          [EventId.ADD_REVIEWER_ROLE]: {
            actions: [
              assign({
                reviewRoles: (context, event) => [...context.reviewRoles, event.role],
              }),
            ],
          },
          [EventId.OPEN_TEMPLATE_FORM]: StateId.TEMPLATE_FORM,
          [EventId.SET_INTERVAL]: {
            actions: [
              assign({
                interval: (context, event) => event.interval,
                weekdays: (context, event) => event.weekdays,
                frequency: (context, event) => event.frequency,
              }),
            ],
          },
          [EventId.SELECT_REVIEW_TEMPLATE]: {
            actions: [assignTemplate(canChangePrivacy)],
          },
        },
      },
      [StateId.INVITE_EMAILS]: {
        on: {
          [EventId.INVITE_GUESTS]: [
            {
              actions: [(context) => submitAction(context, false)],
              cond: (context) => [...context.reviewers, ...context.approvers, ...context.viewers].length > 0,
            },
            {
              actions: [
                (context) => onRequestSuggestion?.(context.suggesters[0], false, context.title, context.message),
              ],
              cond: (context) =>
                context.suggesters.length > 0 && (context.reviewers.length < 1 || context.approvers.length < 1),
            },
          ],
          [EventId.INVITE_TO_WORKSPACE]: [
            {
              actions: [(context) => submitAction(context, true)],
              cond: (context) => [...context.reviewers, ...context.approvers, ...context.viewers].length > 0,
            },
            {
              actions: [
                (context) => onRequestSuggestion?.(context.suggesters[0], true, context.title, context.message),
              ],
              cond: (context) =>
                context.suggesters.length > 0 && (context.reviewers.length < 1 || context.approvers.length < 1),
            },
          ],
          [EventId.BACK]: [
            {
              target: StateId.REVIEWERS_SELECTION,
              cond: (context) => context.reviewers.length > 0 || context.approvers.length > 0,
            },
            {
              target: StateId.SUGGESTERS_SELECTION,
              cond: (context) =>
                context.suggesters.length > 0 && (context.reviewers.length < 1 || context.approvers.length < 1),
            },
          ],
        },
      },
      [StateId.SUGGESTERS_SELECTION]: {
        on: {
          [EventId.SELECT_SUGGESTERS]: {
            actions: [
              assign({ suggesters: (_, event) => event.suggesters }),
              assign({ reviewers: (_) => [] }),
              assign({ approvers: (_) => [] }),
              assign({
                title: (context, event) => {
                  const suggester = event.suggesters[0]
                  if (!suggester || suggester.type !== RecipientsSelectorValueType.User) {
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    return reviewTitle(context.asset!.title, ReviewType.SUGGESTIONS)
                  }

                  const { first_name } = suggester.value

                  if (first_name.trim().length < 1) {
                    // TODO: Fix this the next time the file is edited.
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    return reviewTitle(context.asset!.title, ReviewType.SUGGESTIONS)
                  }

                  // TODO: Fix this the next time the file is edited.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  return `${first_name}'s ${reviewTitle(context.asset!.title, ReviewType.SUGGESTIONS)}`
                },
              }),
            ],
          },
          [EventId.CONTINUE]: [
            {
              target: StateId.INVITE_EMAILS,
              cond: (_, event) => !!event.newEmails && event.newEmails.length > 0,
            },
            {
              actions: [
                (context) => onRequestSuggestion?.(context.suggesters[0], false, context.title, context.message),
              ],
              cond: (_, event) => !event.newEmails || event.newEmails.length < 1,
            },
          ],
          [EventId.BACK]: StateId.TYPE_SELECTION_FETCHING_REVIEW_TYPE_CONFIG,
          [EventId.SET_MESSAGE]: {
            actions: [assign({ message: (context, event) => event.message })],
          },
          [EventId.SET_TITLE]: {
            actions: [assign({ title: (context, event) => event.title })],
          },
        },
      },
      [StateId.TEMPLATE_FORM]: {
        on: {
          [EventId.BACK]: StateId.REVIEWERS_SELECTION,
          [EventId.TEMPLATE_SAVED]: {
            target: StateId.REVIEWERS_SELECTION,
            actions: [assignTemplate(canChangePrivacy)],
          },
        },
      },
    },
  } as MachineConfig<Context, any, ReviewWizardEvent>)
}
